import { CommonModule } from "@angular/common";
import { Component, computed, inject, signal } from "@angular/core";
import {
    AlertService,
    Button,
    Hint,
    Icon,
    ModalService,
    ToastService,
    TooltipDirective,
} from "@malirang/front-share";
import { CenterStore } from "../../../../stores/center/center.store";
import { CenterRoleStore } from "../../../../stores/center/role.store";
import { ChildWithServiceCompletenessComponent } from "./child-with-service-completeness/child-with-service-completeness.component";
import { CreateChildCompletenessComponent } from "./create-child-completeness/create-child-completeness.component";
import { CreateScheduleCompletenessComponent } from "./create-schedule-completeness/create-schedule-completeness.component";
import { CreateServiceCompletenessComponent } from "./create-service-completeness/create-service-completeness.component";
import { CenterService } from "@malirang/api-client";
import { CompletenessGuideModal } from "./completeness-guide/completeness-guide.modal";

@Component({
    selector: "app-center-completeness",
    templateUrl: "./center-completeness.component.html",
    styleUrls: ["./center-completeness.component.css"],
    imports: [
        CommonModule,
        Icon,
        CreateServiceCompletenessComponent,
        CreateScheduleCompletenessComponent,
        CreateChildCompletenessComponent,
        ChildWithServiceCompletenessComponent,
        Button,
        TooltipDirective,
        Hint,
    ],
})
export class CenterCompletenessComponent {
    readonly centerStore = inject(CenterStore);
    readonly centerRoleStore = inject(CenterRoleStore);
    readonly centerService = inject(CenterService);
    readonly alertService = inject(AlertService);
    readonly toastService = inject(ToastService);
    readonly modalService = inject(ModalService);

    currentCenter = this.centerStore.currentCenter;
    isOwner = this.centerRoleStore.isOwner;

    completeness = computed(() => this.currentCenter()?.completeness);

    completedCount = computed(() => {
        if (!this.completeness()) {
            return 0;
        }

        let count = 0;

        if (this.completeness()?.childWithService) count++;
        if (this.completeness()?.createChild) count++;
        if (this.completeness()?.createSchedule) count++;
        if (this.completeness()?.createService) count++;

        return count;
    });

    completeds = computed(() => Array.from({ length: this.completedCount() }));
    incompleteds = computed(
        () => Array.from({ length: 4 - this.completedCount() }), // 총 4개
    );

    completenessPercent = computed(() => {
        if (!this.completeness()) {
            return 0;
        }

        return (this.completedCount() / 4) * 100;
    });

    open = signal(false);

    constructor() {
        this.centerStore.currentCenter$.subscribe({
            next: () => {
                this.open.set(this.completeness()?.completed ? false : true);
            },
        });
    }

    openModal() {
        this.modalService.create(CompletenessGuideModal);
    }

    reStartTutorial() {
        this.alertService
            .open({
                title: "튜토리얼 다시 시작",
                content:
                    "센터 완성도 튜토리얼을 다시 시작합니다. 기존에 등록한 데이터는 초기화됩니다.",
                type: "warning",
                buttons: {
                    confirm: { text: "다시 시작", color: "yellow" },
                },
            })
            .closed.subscribe((result) => {
                if (result?.action === "confirm") {
                    this.centerService
                        .userCenterControllerResetTutorial({
                            centerId: this.currentCenter()?.id || "",
                        })
                        .subscribe({
                            next: () => {
                                this.centerStore.fetchCenters();
                            },
                        });
                }
            });
    }

    reset() {
        this.alertService
            .open({
                title: "등록 데이터 초기화",
                content: "센터에 등록된 데이터를 모두 초기화합니다.",
                type: "warning",
                buttons: {
                    confirm: { text: "초기화", color: "yellow" },
                },
            })
            .closed.subscribe((result) => {
                if (result?.action === "confirm") {
                    this.centerService
                        .userCenterControllerReset({
                            centerId: this.currentCenter()?.id || "",
                        })
                        .subscribe({
                            next: () => {
                                this.centerStore.fetchCenters();
                            },
                        });
                }
            });
    }

    confirm() {
        this.alertService
            .open({
                title: "등록 데이터 저장",
                content: "데이터를 저장하시겠습니까 ?",
                type: "info",
            })
            .closed.subscribe((result) => {
                if (result?.action === "confirm") {
                    this.centerService
                        .userCenterControllerConfirmTutorial({
                            centerId: this.currentCenter()?.id || "",
                        })
                        .subscribe({
                            next: () => {
                                this.centerStore.fetchCenters();
                            },
                        });
                }
            });
    }
}
